import { useEffect, useState } from 'react';
import * as nominationService from '../../../services/nominationCaseRequests';
import type { LookupItem } from '../../../mocks/criminalMasterData';

export type NominationLookupScope = 'step2' | 'step3' | 'step4' | 'review';

type LookupBundle = {
  urgencyLevels: LookupItem[];
  deliveryMethods: LookupItem[];
};

const EMPTY: LookupBundle = {
  urgencyLevels: [],
  deliveryMethods: [],
};

let sharedLookups: LookupBundle = { ...EMPTY };

export function useNominationLookups(scope: NominationLookupScope) {
  const [data, setData] = useState<LookupBundle>(sharedLookups);
  const [loading, setLoading] = useState(scope !== 'step4');

  useEffect(() => {
    if (scope === 'step4') {
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);

    const load = async (): Promise<Partial<LookupBundle>> => {
      if (scope === 'step2') {
        const urg = await nominationService.getUrgencyLevels();
        return { urgencyLevels: urg.success ? urg.data : [] };
      }
      if (scope === 'step3') {
        const del = await nominationService.getDeliveryMethods();
        return { deliveryMethods: del.success ? del.data : [] };
      }
      const [urg, del] = await Promise.all([
        nominationService.getUrgencyLevels(),
        nominationService.getDeliveryMethods(),
      ]);
      return {
        urgencyLevels: urg.success ? urg.data : [],
        deliveryMethods: del.success ? del.data : [],
      };
    };

    void load().then((partial) => {
      if (!mounted) return;
      sharedLookups = { ...sharedLookups, ...partial };
      setData({ ...sharedLookups });
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [scope]);

  return {
    loading,
    urgencyLevels: data.urgencyLevels,
    deliveryMethods: data.deliveryMethods,
  };
}
